const EPSILON = 0.001;
let sessionCounter = 0;

function mergeRanges(ranges) {
  const sorted = ranges.filter(r => Array.isArray(r) && Number.isFinite(r[0]) && Number.isFinite(r[1]) && r[1] > r[0])
    .map(([a, b]) => [Math.max(0, a), b]).sort((a, b) => a[0] - b[0]);
  const result = [];
  for (const range of sorted) {
    const previous = result.at(-1);
    if (previous && range[0] <= previous[1] + EPSILON) previous[1] = Math.max(previous[1], range[1]);
    else result.push(range);
  }
  return result;
}

function sessionId() {
  return globalThis.crypto?.randomUUID?.() || `audio-${Date.now()}-${++sessionCounter}`;
}

/** One persistent media element. All progress callbacks belong to the loaded language/session. */
export function createPlayer({audioElement, onChange, onCue, onProgress, onComplete} = {}) {
  const audio = audioElement || new Audio();
  let generation = 0;
  let program = null;
  let languageId = null;
  let session = null;
  let position = 0;
  let duration = 0;
  let rate = Number(audio.playbackRate) || 1;
  let playing = false;
  let error = null;
  let completed = false;
  let credited = new Set();
  let playedRanges = [];
  let anchor = 0;
  let seeking = false;
  let pendingSeek = null;
  let changingSource = false;

  const safely = (callback, ...args) => { try { callback?.(...args); } catch { /* Consumer failures must not stop playback. */ } };
  const context = () => ({languageId, program, sessionId: session});
  const snapshot = () => ({programId: program?.id ?? null, position, creditedCueIds: [...credited], playedRanges: playedRanges.map(r => [...r]), sessionId: session, completed});
  const getState = () => ({program, languageId, playing, position, duration, rate, error, snapshot: program ? snapshot() : null});
  const currentPosition = () => Number.isFinite(audio.currentTime) ? Math.max(0, audio.currentTime) : position;
  const learningCues = () => (program?.cues || []).filter(c => c.kind === 'audio').map(c =>
    program.singleClip && !(c.end > c.start) && duration > 0 ? {...c, end: duration} : c);
  const covered = cue => Number.isFinite(cue.start) && Number.isFinite(cue.end) && cue.end > cue.start &&
    playedRanges.some(([start, end]) => start <= cue.start + EPSILON && end >= cue.end - EPSILON);

  function nativeRanges() {
    if (!program?.src) return [];
    const result = [];
    try { for (let i = 0; i < audio.played.length; i++) result.push([audio.played.start(i), audio.played.end(i)]); } catch { /* A source replacement can invalidate TimeRanges. */ }
    return mergeRanges(result);
  }

  function listeningBetween(start, end, ranges) {
    if (end <= start) return 0;
    const pauses = mergeRanges((program?.cues || []).filter(c => c.kind === 'pause').map(c => [c.start, c.end]));
    let seconds = 0;
    for (const [a, b] of ranges) {
      const low = Math.max(a, start), high = Math.min(b, end);
      if (high <= low) continue;
      seconds += high - low;
      for (const [p, q] of pauses) seconds -= Math.max(0, Math.min(high, q) - Math.max(low, p));
    }
    return Math.max(0, seconds) / rate;
  }

  function updateMediaSession() {
    const media = globalThis.navigator?.mediaSession;
    if (!media) return;
    try { media.playbackState = playing ? 'playing' : program ? 'paused' : 'none'; } catch { /* Optional platform API. */ }
    try {
      if (program && duration > 0 && Number.isFinite(duration)) media.setPositionState?.({duration, playbackRate: rate, position: Math.min(position, duration)});
      else media.setPositionState?.();
    } catch { /* Metadata may not be loaded yet. */ }
  }

  function emitChange() { updateMediaSession(); safely(onChange, getState()); }

  function reconcile({skipListening = false} = {}) {
    if (!program || changingSource) return;
    const ownGeneration = generation;
    const ownContext = context();
    // A saved resume position may have to wait for metadata before currentTime accepts it.
    const now = pendingSeek ?? currentPosition();
    const ranges = nativeRanges();
    const listeningDelta = !skipListening && playing && !seeking ? listeningBetween(anchor, now, ranges) : 0;
    playedRanges = mergeRanges([...playedRanges, ...ranges]);
    position = now;
    anchor = now;
    const newlyCredited = [];
    for (const cue of learningCues()) {
      if (!credited.has(cue.id) && covered(cue)) { credited.add(cue.id); newlyCredited.push(cue); }
    }
    const cues = learningCues();
    const justCompleted = !completed && cues.length > 0 && cues.every(covered);
    if (justCompleted) completed = true;
    // Save before emitting exposure/completion callbacks, so their consumers see the checkpoint.
    safely(onProgress, {...snapshot(), listeningDelta}, ownContext);
    for (const cue of newlyCredited) safely(onCue, cue, ownContext);
    if (justCompleted) safely(onComplete, ownContext.program, ownContext);
    if (generation === ownGeneration) emitChange();
  }

  function refreshDuration() {
    const actual = Number(audio.duration);
    duration = Number.isFinite(actual) && actual > 0 ? actual : Number(program?.duration) || 0;
  }

  function applyPosition(seconds) {
    const target = Math.max(0, duration > 0 ? Math.min(seconds, duration) : seconds);
    seeking = true;
    pendingSeek = target;
    position = target;
    anchor = target;
    try {
      audio.currentTime = target;
      if (Math.abs(currentPosition() - target) < EPSILON) pendingSeek = null;
    } catch { /* Retry when loadedmetadata arrives, notably on iOS. */ }
    seeking = Boolean(audio.seeking) || pendingSeek !== null;
  }

  async function play() {
    if (!program || !program.src) return false;
    const ownGeneration = generation;
    error = null;
    try {
      await audio.play();
      if (generation !== ownGeneration) return false;
      playing = !audio.paused && !audio.ended;
      emitChange();
      return true;
    } catch (cause) {
      if (generation !== ownGeneration) return false;
      playing = false;
      error = cause?.message || 'Audio could not be played.';
      emitChange();
      return false;
    }
  }

  function pause() {
    reconcile();
    audio.pause();
    playing = false;
    emitChange();
  }

  async function load(nextProgram, {languageId: nextLanguage = null, resume = null, autoplay = false} = {}) {
    reconcile();
    generation++;
    changingSource = true;
    audio.pause();
    program = nextProgram;
    languageId = nextLanguage;
    const saved = resume?.programId === program?.id ? resume : null;
    session = saved?.sessionId || sessionId();
    credited = new Set((saved?.creditedCueIds || []).filter(id => learningCues().some(c => c.id === id)));
    playedRanges = mergeRanges(saved?.playedRanges || []);
    const cues = learningCues();
    completed = cues.length > 0 && cues.every(covered);
    position = Number.isFinite(saved?.position) ? Math.max(0, saved.position) : 0;
    anchor = position;
    duration = Number(program?.duration) || 0;
    playing = false;
    error = null;
    seeking = false;
    pendingSeek = null;
    try {
      if (!program?.src) {
        audio.removeAttribute?.('src');
        audio.load();
        throw new Error('This audio program has no audio file.');
      }
      audio.src = program.src;
      audio.preload = 'metadata';
      audio.load();
      audio.playbackRate = rate;
      applyPosition(position);
    } catch (cause) { error = cause?.message || 'Audio could not be loaded.'; }
    changingSource = false;
    const media = globalThis.navigator?.mediaSession;
    try { if (media && globalThis.MediaMetadata) media.metadata = new MediaMetadata({title: program?.title || '', artist: 'Hibiki'}); } catch { /* Optional platform API. */ }
    safely(onProgress, {...snapshot(), listeningDelta: 0}, context());
    emitChange();
    return autoplay && !error ? play() : !error;
  }

  function seek(seconds) {
    if (!program || !Number.isFinite(seconds)) return;
    reconcile();
    applyPosition(seconds);
    safely(onProgress, {...snapshot(), listeningDelta: 0}, context());
    emitChange();
  }

  function setRate(nextRate) {
    if (!Number.isFinite(nextRate) || nextRate <= 0) return;
    reconcile();
    try { audio.playbackRate = nextRate; rate = Number(audio.playbackRate) || nextRate; } catch { return; }
    emitChange();
  }

  function restart() {
    if (!program) return Promise.resolve(false);
    return load(program, {languageId, autoplay: true});
  }

  function stop() {
    reconcile();
    generation++;
    changingSource = true;
    audio.pause();
    try { audio.removeAttribute('src'); audio.load(); } catch { /* Fake or disconnected media. */ }
    program = null;
    languageId = null;
    playing = false;
    position = duration = anchor = 0;
    error = null;
    pendingSeek = null;
    changingSource = false;
    try { if (globalThis.navigator?.mediaSession) globalThis.navigator.mediaSession.metadata = null; } catch { /* Optional platform API. */ }
    emitChange();
  }

  const listen = (name, handler) => audio.addEventListener(name, () => { if (program && !changingSource) handler(); });
  listen('timeupdate', () => reconcile());
  listen('play', () => { if (!audio.paused) { playing = true; emitChange(); } });
  listen('playing', () => { if (!audio.paused) { playing = true; emitChange(); } });
  // Native events are queued: a pause from the previous source can arrive after load/play.
  listen('pause', () => { if (audio.paused) { reconcile(); playing = false; emitChange(); } });
  listen('ended', () => { if (audio.ended) { reconcile(); playing = false; emitChange(); } });
  listen('seeking', () => { reconcile({skipListening: true}); seeking = true; anchor = currentPosition(); });
  listen('seeked', () => { seeking = false; pendingSeek = null; anchor = currentPosition(); reconcile({skipListening: true}); });
  listen('ratechange', () => { reconcile(); rate = Number(audio.playbackRate) || 1; emitChange(); });
  listen('loadedmetadata', () => { refreshDuration(); if (pendingSeek !== null) applyPosition(pendingSeek); emitChange(); });
  listen('durationchange', () => { refreshDuration(); emitChange(); });
  listen('error', () => {
    if (!audio.error) return;
    reconcile();
    playing = false;
    error = audio.error?.message || 'Audio could not be loaded. Check the connection or audio file.';
    emitChange();
  });
  globalThis.document?.addEventListener?.('visibilitychange', () => reconcile());
  globalThis.window?.addEventListener?.('pagehide', () => reconcile());

  const media = globalThis.navigator?.mediaSession;
  if (media?.setActionHandler) {
    const handlers = {
      play, pause, stop,
      seekto: details => seek(details.seekTime),
      seekbackward: details => seek(position - (details.seekOffset || 10)),
      seekforward: details => seek(position + (details.seekOffset || 10)),
    };
    for (const [action, handler] of Object.entries(handlers)) {
      try { media.setActionHandler(action, handler); } catch { /* Each action has independent browser support. */ }
    }
  }
  return {load, play, pause, seek, setRate, restart, stop, getState};
}
